import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import type {
  TraceListItem,
  TraceEntityHydrationStatus,
  TraceInputVariables,
} from "@/types/traces";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

type TraceEntityId = number | string;

export interface WorkflowInputVariableScope {
  workflowId: string;
  workflowVersionNumber: string;
}

export interface TraceEntityHydrationResponse {
  success: boolean;
  prompt_names_by_id: Record<string, string>;
  workflow_names_by_id: Record<string, string>;
  input_variables_by_trace_id?: Record<string, TraceInputVariables>;
}

export interface TraceEntityHydrationIds {
  promptIds: string[];
  workflowIds: string[];
  traceIds: string[];
}

interface FetchTraceEntityHydrationArgs {
  signal?: AbortSignal;
  userToken: string;
  workspaceId: number;
  promptIds: string[];
  workflowIds: string[];
  traceIds: string[];
  workflowInputVariableScope?: WorkflowInputVariableScope;
}

interface CollectTraceEntityHydrationIdsOptions {
  includeInputVariables?: boolean;
}

interface UseTraceEntityHydrationArgs {
  userToken: string;
  workspaceId: number;
  items: TraceListItem[];
  enabled?: boolean;
}

const toNumericTraceEntityId = (value: unknown): string | null => {
  if (typeof value === "boolean" || value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 ? String(value) : null;
  }

  const text = String(value).trim();
  return /^\d+$/.test(text) ? text : null;
};

const collectIds = (values: TraceEntityId[] | null | undefined): string[] =>
  values?.map(toNumericTraceEntityId).filter((value): value is string => value !== null) ?? [];

const uniqueSorted = (values: string[]): string[] => Array.from(new Set(values)).sort();

export const collectTraceEntityHydrationIds = (
  items: TraceListItem[],
  options?: CollectTraceEntityHydrationIdsOptions,
): TraceEntityHydrationIds => ({
  promptIds: uniqueSorted(items.flatMap(item => collectIds(item.prompt_ids))),
  workflowIds: uniqueSorted(items.flatMap(item => collectIds(item.workflow_ids))),
  traceIds: options?.includeInputVariables
    ? uniqueSorted(items.map(item => item.trace_id).filter(Boolean))
    : [],
});

export const hasTraceEntityHydrationIds = ({
  promptIds,
  workflowIds,
  traceIds,
}: TraceEntityHydrationIds) =>
  promptIds.length > 0 || workflowIds.length > 0 || traceIds.length > 0;

export const getTraceEntityHydrationQueryKeyPrefix = (workspaceId: number) =>
  [queryKeys.traces, "entity-hydration", workspaceId] as const;

export const getTraceEntityHydrationQueryKey = ({
  workspaceId,
  promptIds,
  workflowIds,
  traceIds,
  workflowInputVariableScope,
}: {
  workspaceId: number;
  promptIds: string[];
  workflowIds: string[];
  traceIds: string[];
  workflowInputVariableScope?: WorkflowInputVariableScope;
}) =>
  [
    ...getTraceEntityHydrationQueryKeyPrefix(workspaceId),
    promptIds.join(","),
    workflowIds.join(","),
    traceIds.join(","),
    workflowInputVariableScope?.workflowId ?? "",
    workflowInputVariableScope?.workflowVersionNumber ?? "",
  ] as const;

export const fetchTraceEntityHydration = async ({
  signal,
  userToken,
  workspaceId,
  promptIds,
  workflowIds,
  traceIds,
  workflowInputVariableScope,
}: FetchTraceEntityHydrationArgs): Promise<TraceEntityHydrationResponse> => {
  const response = await fetch(ENDPOINTS.traces_entity_hydration(workspaceId), {
    method: "POST",
    headers: { ...authHeader(userToken), "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      prompt_ids: promptIds,
      workflow_ids: workflowIds,
      trace_ids: traceIds,
      workflow_id: workflowInputVariableScope?.workflowId,
      workflow_version_number: workflowInputVariableScope?.workflowVersionNumber,
    }),
    signal,
  });

  return handleResponse(response);
};

const resolveEntityNames = (
  values: TraceEntityId[] | null | undefined,
  namesById: Record<string, string>,
): string[] | null => {
  if (!values) {
    return values ?? null;
  }

  return values.map(value => {
    const numericId = toNumericTraceEntityId(value);
    return numericId && namesById[numericId] ? namesById[numericId] : String(value);
  });
};

const itemHasHydratableEntities = (item: TraceListItem): boolean =>
  collectIds(item.prompt_ids).length > 0 || collectIds(item.workflow_ids).length > 0;

export const applyTraceEntityHydrationToItems = ({
  items,
  hydration,
  status,
  includeInputVariables = false,
}: {
  items: TraceListItem[];
  hydration?: TraceEntityHydrationResponse;
  status: TraceEntityHydrationStatus;
  includeInputVariables?: boolean;
}): TraceListItem[] =>
  items.map(item => {
    const inputVariables = includeInputVariables
      ? (hydration?.input_variables_by_trace_id?.[item.trace_id] ?? null)
      : item.input_variables;

    if (!itemHasHydratableEntities(item)) {
      return {
        ...item,
        input_variables: inputVariables,
        entity_hydration_status:
          includeInputVariables && status !== "complete" ? status : "complete",
      };
    }

    return {
      ...item,
      input_variables: inputVariables,
      hydrated_prompt_names: resolveEntityNames(
        item.prompt_ids,
        hydration?.prompt_names_by_id ?? {},
      ),
      hydrated_workflow_names: resolveEntityNames(
        item.workflow_ids,
        hydration?.workflow_names_by_id ?? {},
      ),
      entity_hydration_status: status,
    };
  });

export const useTraceEntityHydration = ({
  userToken,
  workspaceId,
  items,
  enabled = true,
}: UseTraceEntityHydrationArgs) => {
  const ids = useMemo(() => collectTraceEntityHydrationIds(items), [items]);
  const queryEnabled = enabled && !!userToken && workspaceId > 0 && hasTraceEntityHydrationIds(ids);

  return useQuery({
    queryKey: getTraceEntityHydrationQueryKey({
      workspaceId,
      promptIds: ids.promptIds,
      workflowIds: ids.workflowIds,
      traceIds: ids.traceIds,
    }),
    queryFn: () =>
      fetchTraceEntityHydration({
        userToken,
        workspaceId,
        promptIds: ids.promptIds,
        workflowIds: ids.workflowIds,
        traceIds: ids.traceIds,
      }),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });
};
